/** Colour maths for WCAG contrast (used by data-coloured chips and the brand contrast tests). */
import { SETTINGS_PALETTE } from '@tradeoutlet/shared';
export type Rgb = [number, number, number];

export function parseHex(h: string): Rgb | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const s = m[1].length === 3 ? m[1].replace(/./g, c => c + c) : m[1];
  const n = parseInt(s, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function luminance([r, g, b]: Rgb) {
  const c = (v: number) => { const s = v / 255; return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

export function contrastRatio(a: Rgb, b: Rgb) {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const INK: Rgb = [10, 10, 10];
/**
 * Text colour for a chip filled with a user/data-chosen colour (leave types, statuses, positions,
 * projects): white when it reaches AA on that fill, otherwise near-black.
 */
export function readableTextOn(bg: string | null | undefined): string {
  const rgb = bg ? parseHex(bg) : null;
  if (!rgb) return '#ffffff';
  return contrastRatio(WHITE, rgb) >= 4.5 || contrastRatio(WHITE, rgb) >= contrastRatio(INK, rgb) ? '#ffffff' : '#0a0a0a';
}

function toHsl([r, g, b]: Rgb): [number, number, number] {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const mx = Math.max(R, G, B), mn = Math.min(R, G, B), l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn, s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  const h = mx === R ? ((G - B) / d + (G < B ? 6 : 0)) : mx === G ? (B - R) / d + 2 : (R - G) / d + 4;
  return [h * 60, s, l];
}
const hsl = (h: number, s: number, l: number) => `hsl(${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;

/**
 * Schedule tones for a position colour (admin-set): a soft pastel fill, a solid but muted left border and dark
 * text. Brand rule: the brand yellow is reserved for primary actions, so yellow-ish hues (44-70 deg) are nudged to amber.
 */
/** Browsers that understand light-dark() get chips that follow the theme (the .dark root sets color-scheme). */
const ADAPTIVE = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('color', 'light-dark(#000, #fff)');

export function positionTone(hex: string | null | undefined, adaptive = ADAPTIVE): { bg: string; border: string; text: string } {
  const rgb = parseHex(hex ?? '') ?? [99, 102, 241];
  let [h, s] = toHsl(rgb);
  if (h >= 44 && h <= 70 && s > 0.25) h = 36;
  s = Math.min(s, 0.65);
  const bg = hsl(h, Math.min(s, 0.55), 0.93), text = hsl(h, Math.min(s, 0.5), 0.2);
  // UI review batch 4 (dark mode): a deep tint and light text in dark mode instead of a pastel card.
  if (adaptive) return { bg: `light-dark(${bg}, ${hsl(h, Math.min(s, 0.4), 0.2)})`, border: hsl(h, s, 0.5), text: `light-dark(${text}, ${hsl(h, Math.min(s, 0.5), 0.88)})` };
  return { bg, border: hsl(h, s, 0.5), text };
}

/** Soft, brand-safe defaults offered when admins pick a position colour (none near the brand yellow). */
export const POSITION_PALETTE: string[] = SETTINGS_PALETTE.slice(0, 8);
